// In-game chat, in the spirit of CS:S: Y opens a "Say :" line at the bottom left, Enter sends,
// Esc closes. Lines starting with ! are commands (handled by onSay's owner). While closed, recent
// lines stay a few seconds and fade, like in CS:S. The frame is painted with the knife's skin.

import { drawFinish } from '../render/finishTexture';
import type { Finish } from '../render/finishes';

/** Seconds a line stays on screen while the chat is closed. */
const LINE_SECONDS = 12;
const MAX_LINES = 60;

export type ChatTone = 'say' | 'system' | 'error';

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, ...kids: (Node | string)[]) {
  const e = document.createElement(tag);
  e.className = className;
  e.append(...kids);
  return e;
}

export class Chat {
  private readonly root = el('div', 'chat');
  private readonly log = el('div', 'chatLog');
  private readonly input = el('input', 'chatInput');
  private readonly skins = new Map<Finish, string>();
  /** Called with what the player typed (trimmed, not empty). */
  onSay: (text: string) => void = () => {};
  /** Called when the chat closes (the game takes the keyboard back). */
  onClose: () => void = () => {};
  /** Typed lines, for Up/Down. */
  private readonly history: string[] = [];
  private historyAt = 0;

  constructor(parent: HTMLElement) {
    this.input.type = 'text';
    this.input.maxLength = 127;
    this.input.spellcheck = false;
    this.input.autocomplete = 'off';
    this.input.setAttribute('aria-label', 'Chat message or command');
    const head = el(
      'div',
      'chatHead',
      el('span', '', 'Chat'),
      el('span', 'chatHint', 'Type !help for commands'),
    );
    const row = el('label', 'chatRow', el('span', 'chatSay', 'Say :'), this.input);
    const inner = el('div', 'chatInner', head, this.log, row);
    this.root.append(inner);
    parent.append(this.root);

    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        const text = this.input.value.trim();
        if (text) {
          this.history.push(text);
          if (this.history.length > 30) this.history.shift();
        }
        this.close();
        if (text) this.onSay(text);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      } else if (e.key === 'PageUp' || e.key === 'PageDown') {
        e.preventDefault();
        this.scroll((e.key === 'PageUp' ? -1 : 1) * this.log.clientHeight * 0.8);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        if (!this.history.length) return;
        e.preventDefault();
        const step = e.key === 'ArrowUp' ? -1 : 1;
        this.historyAt = Math.max(0, Math.min(this.history.length, this.historyAt + step));
        this.input.value = this.history[this.historyAt] ?? '';
      }
    });
    // Clicking elsewhere (or the browser taking focus) closes it.
    this.input.addEventListener('blur', () => {
      if (this.isOpen) this.close();
    });
  }

  get isOpen() {
    return this.root.classList.contains('open');
  }

  /** Shown while playing, hidden in the menus. */
  setShown(on: boolean) {
    this.root.hidden = !on;
    if (!on) this.close();
  }

  open() {
    if (this.isOpen) return;
    this.root.classList.add('open');
    this.input.value = '';
    this.historyAt = this.history.length;
    this.input.focus({ preventScroll: true });
    this.log.scrollTop = this.log.scrollHeight;
  }

  close() {
    if (!this.isOpen) return;
    this.root.classList.remove('open');
    this.input.blur();
    this.onClose();
  }

  /** Scroll the open chat's lines (the mouse wheel while it's open; the mouse is still captured). */
  scroll(dy: number) {
    this.log.scrollTop += dy;
  }

  /** Add a line: \`name\` (a player) before the text, or a system tag for messages from the game. */
  add(text: string, tone: ChatTone = 'system', name?: string) {
    const line = el('div', 'chatLine ' + tone);
    if (name) line.append(el('b', 'chatName', name), ': ');
    else line.append(el('b', 'chatTag', '[bhop]'), ' ');
    line.append(text);
    // Reading older lines: a new one doesn't jump back to the bottom.
    const atBottom = this.log.scrollTop + this.log.clientHeight >= this.log.scrollHeight - 4;
    this.log.append(line);
    while (this.log.childElementCount > MAX_LINES) this.log.firstElementChild!.remove();
    if (atBottom || !this.isOpen) this.log.scrollTop = this.log.scrollHeight;
    // Fades out after a while (only matters while the chat is closed).
    setTimeout(() => line.classList.add('old'), LINE_SECONDS * 1000);
  }

  /** Paint the frame with a knife skin. */
  setSkin(f: Finish) {
    let url = this.skins.get(f);
    if (!url) {
      const c = drawFinish(f);
      // A small copy is plenty for a 4 px frame.
      const s = document.createElement('canvas');
      s.width = s.height = 128;
      s.getContext('2d')!.drawImage(c, 0, 0, 128, 128);
      url = s.toDataURL('image/jpeg', 0.85);
      this.skins.set(f, url);
    }
    this.root.style.setProperty('--chat-skin', `url(${url})`);
  }
}
